import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  getTenantFeatures,
  patchTenantFeatures,
  type Product,
  type TenantFeatures,
} from '../lib/api';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { useProductSearch } from '../lib/use-product-search';
import { useProductsByIds } from '../lib/use-products-by-ids';

/** ISO 8601 ⇄ <input type="datetime-local"> (local wall-clock) helpers. */
function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Flash Sale merchandising. The admin toggles the storefront "Flash Sale" strip,
 * picks the products to feature (order = display order), and an optional countdown
 * end time. Persisted in tenant_config.features.flash_sale (super_admin write).
 */
export function FlashSalePage() {
  const queryClient = useQueryClient();
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [enabled, setEnabled] = useState(false);
  const [endsAtLocal, setEndsAtLocal] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [hydrated, setHydrated] = useState(false);

  // Seed local draft from the server config once.
  useEffect(() => {
    if (featuresQ.data && !hydrated) {
      const fs = featuresQ.data.flash_sale;
      setEnabled(fs.enabled);
      setSelectedIds(fs.productIds);
      setEndsAtLocal(isoToLocalInput(fs.endsAt));
      setHydrated(true);
    }
  }, [featuresQ.data, hydrated]);

  const save = useMutation({
    meta: { successMessage: 'Flash sale saved.' },
    mutationFn: (patch: Partial<TenantFeatures>) => patchTenantFeatures(patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  // Candidates come from the SERVER (FTS + trigram, top 20 by relevance). The
  // old shape — one `pageSize: 200` catalog page filtered in the browser — made
  // every product past that page unreachable while looking like it worked.
  const { results, isLoading: searching } = useProductSearch({ term: search });
  // The saved selection is resolved BY ID, archived included. Resolving it out of
  // the search page instead silently dropped rows that were still saved (and so
  // could not be removed).
  const { byId } = useProductsByIds(selectedIds);
  const selected = selectedIds.map((id) => byId.get(id)).filter((p): p is Product => Boolean(p));
  const filtered = results.filter((p) => !selectedIds.includes(p.id));

  function add(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }
  function removeId(id: string) {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  }
  // Build the flash_sale config once — used for both the confirm-dialog diff and
  // the mutation so the preview matches what gets saved.
  function buildFlashSale() {
    return { enabled, productIds: selectedIds, endsAt: localInputToIso(endsAtLocal) };
  }

  const errMsg = (save.error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Flash Sale</h1>
        <ConfirmSaveButton
          label="Save"
          title="Save Flash Sale"
          current={featuresQ.data?.flash_sale}
          next={buildFlashSale()}
          loading={save.isPending}
          disabled={!hydrated}
          onConfirm={() => save.mutateAsync({ flash_sale: buildFlashSale() })}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Controls the storefront home "Flash Sale" strip. Pick the products to feature and an
        optional countdown end. When off or empty, the strip is hidden on the storefront.
      </p>
      {save.isSuccess && !save.isPending && <p className="text-sm text-success">Saved.</p>}
      {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4"
            />
            Flash sale enabled
          </label>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Ends at (optional countdown)</label>
            <div className="flex items-center gap-2">
              <Input
                type="datetime-local"
                value={endsAtLocal}
                onChange={(e) => setEndsAtLocal(e.target.value)}
                className="max-w-xs"
              />
              {endsAtLocal && (
                <Button variant="outline" size="sm" onClick={() => setEndsAtLocal('')}>
                  Clear
                </Button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">
              Leave blank for no countdown. The storefront hides the strip once this time passes.
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Selected (ordered) */}
        <Card>
          <CardHeader>
            <CardTitle>Featured products ({selected.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {selected.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No products selected. Add some from the list →
              </p>
            ) : (
              <ul className="divide-y">
                {selected.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-2 py-2">
                    <span className="w-5 text-xs text-muted-foreground">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {p.sku} · ₱{Number(p.price).toLocaleString()}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => removeId(p.id)}>
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Picker */}
        <Card>
          <CardHeader>
            <CardTitle>Add products</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder="Search by name or SKU…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {searching ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {search.trim() ? 'No matches.' : 'No products yet — add products first.'}
              </p>
            ) : (
              <ul className="max-h-80 divide-y overflow-y-auto">
                {filtered.map((p) => (
                  <li key={p.id} className="flex items-center gap-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm">{p.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {p.sku} · ₱{Number(p.price).toLocaleString()}
                        {!p.active && ' · inactive'}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => add(p.id)}>
                      Add
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
